import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    FiMessageSquare,
    FiMaximize2,
    FiMinimize2,
    FiX,
    FiSend,
    FiLoader,
    FiHelpCircle,
    FiCheckCircle,
} from "react-icons/fi";
import { questionChatApi, assessmentsApi } from "../services/api";
import { useAuth } from "../context/AuthContext";

// Poll interval while a thread is open (ms). Simple v1 live-update strategy.
const POLL_MS = 5000;

// Format an ISO/UTC timestamp as "10:24 AM".
const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Two-letter initials for the avatar bubble.
const initials = (name, role) => {
    if (name && name.trim()) {
        const parts = name.trim().split(/\s+/);
        return (
            (parts[0]?.[0] || "") + (parts[1]?.[0] || parts[0]?.[1] || "")
        ).toUpperCase();
    }
    return role === "reviewer" ? "RV" : "ME";
};

/**
 * Per-question "Query" chat between the assessment owner (user) and the reviewer.
 * Renders the full chat body (header, question context, messages, input) and
 * manages loading, polling, and sending internally.
 *
 * `senderRole` is the current viewer's side ("user" | "reviewer"): messages with
 * a matching role render as outgoing (right), the other side renders incoming.
 *
 * The user drives the thread's lifecycle: "Need More Information" opens the
 * composer, "Accept" closes the thread (all messages → status 2) and the panel.
 * Only one of the two buttons is ever shown. The reviewer sees neither — once the
 * thread is closed they see an "Accepted by user" notice and cannot reply.
 */
const QuestionChat = ({
    assessmentId,
    questionId,
    answerId = null,
    questionText,
    senderRole,
    open = true,
    chatMaximized,
    setChatMaximized,
    onClose,
}) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [isClosed, setIsClosed] = useState(false);
    // Set when the user clicks "Need More Information" on a thread that has no
    // open messages yet, so the composer shows before the first message exists.
    const [composing, setComposing] = useState(false);
    const [draft, setDraft] = useState("");
    const [error, setError] = useState(null);

    const scrollRef = useRef(null);
    const canLoad = open && assessmentId != null && questionId != null;
    const isUser = senderRole !== "reviewer";

    const loadMessages = useCallback(
        async (showSpinner = false) => {
            if (assessmentId == null || questionId == null) return;
            if (showSpinner) setLoading(true);
            try {
                const data = await questionChatApi.getMessages(
                    assessmentId,
                    questionId,
                );
                // Server returns { messages, isClosed }; tolerate a bare array.
                const list = Array.isArray(data) ? data : data?.messages || [];
                const closed = Array.isArray(data) ? false : !!data?.isClosed;
                setMessages(list);
                setIsClosed(closed);
                // NB: `composing` is deliberately left alone here — the thread is
                // still closed server-side until the user actually sends, so
                // resetting it would snap the composer shut on the next poll.
                setError(null);
            } catch (err) {
                console.error("Failed to load chat messages", err);
                setError("Couldn't load messages.");
            } finally {
                if (showSpinner) setLoading(false);
            }
        },
        [assessmentId, questionId],
    );

    // Initial load + polling while the thread is open.
    useEffect(() => {
        if (!canLoad) return;
        loadMessages(true);
        const id = setInterval(() => loadMessages(false), POLL_MS);
        return () => clearInterval(id);
    }, [canLoad, loadMessages]);

    // Reopening the panel starts from the buttons again, not a stale composer.
    useEffect(() => {
        if (!open) {
            setComposing(false);
            setDraft("");
        }
    }, [open]);

    // Keep the view pinned to the newest message.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async () => {
        const text = draft.trim();
        if (!text || sending || assessmentId == null || questionId == null)
            return;
        setSending(true);
        try {
            const created = await questionChatApi.sendMessage({
                assessmentId,
                questionId,
                answerId,
                senderRole,
                messageText: text,
            });
            setMessages((prev) => [...prev, created]);
            setDraft("");
            // A new message reopens the thread on both sides.
            setIsClosed(false);
            setError(null);

            // A "Need More Information" message means the user isn't satisfied
            // with the reviewed answers yet — mirror that at the assessment level.
            if (isUser) {
                try {
                    await assessmentsApi.userReject(
                        assessmentId,
                        user?.email || null,
                    );
                } catch (err) {
                    console.error(
                        "Failed to flag assessment as rejected after chat message",
                        err,
                    );
                }
            }
        } catch (err) {
            console.error("Failed to send message", err);
            setError("Couldn't send. Try again.");
        } finally {
            setSending(false);
        }
    };

    // User ends the thread: every message is flagged accepted (status 2) and the
    // panel closes. Re-opening the panel shows "Need More Information" again.
    const handleAccept = async () => {
        if (accepting || assessmentId == null || questionId == null) return;
        setAccepting(true);
        try {
            await questionChatApi.acceptThread({ assessmentId, questionId });
            setIsClosed(true);
            setComposing(false);
            setDraft("");
            setError(null);
            onClose?.();
        } catch (err) {
            console.error("Failed to accept conversation", err);
            setError("Couldn't accept. Try again.");
        } finally {
            setAccepting(false);
        }
    };

    // The user's composer is live only while there is an unaccepted conversation:
    // either they just clicked "Need More Information" or messages are still open.
    const threadOpen = composing || (messages.length > 0 && !isClosed);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#0077b5] flex-shrink-0">
                <div className="flex items-center gap-2">
                    <FiMessageSquare className="text-white text-base" />
                    <div>
                        <h4 className="text-sm font-bold text-white leading-none">
                            Query
                        </h4>
                        <p className="text-[10px] text-white/70 mt-0.5">
                            Discuss this question
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setChatMaximized((m) => !m)}
                        className="p-1 rounded-lg hover:bg-white/20 text-white transition-colors"
                        title={chatMaximized ? "Minimize" : "Maximize"}
                    >
                        {chatMaximized ? (
                            <FiMinimize2 className="text-base" />
                        ) : (
                            <FiMaximize2 className="text-base" />
                        )}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-white/20 text-white transition-colors"
                        title="Close"
                    >
                        <FiX className="text-base" />
                    </button>
                </div>
            </div>

            {/* Question context strip */}
            <div className="px-4 py-2.5 bg-[#f0f7ff] border-b border-[#e0e2e6] flex-shrink-0">
                <p className="text-[10px] font-semibold text-[#919191] uppercase mb-0.5">
                    Question
                </p>
                <p
                    className={`text-xs text-[#53545a] font-medium ${
                        chatMaximized ? "" : "line-clamp-2"
                    }`}
                >
                    {questionText}
                </p>
            </div>

            {/* Messages area */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4 space-y-4 bg-[#f7f8fa]"
            >
                <div
                    className={`mx-auto space-y-4 ${
                        chatMaximized ? "max-w-2xl" : ""
                    }`}
                >
                    {loading && messages.length === 0 && (
                        <div className="flex justify-center py-6 text-[#919191]">
                            <FiLoader className="animate-spin text-lg" />
                        </div>
                    )}

                    {!loading && messages.length === 0 && (
                        <p className="text-center text-xs text-[#919191] py-6">
                            No messages yet. Start the conversation.
                        </p>
                    )}

                    {messages.map((m) => {
                        const outgoing = m.senderRole === senderRole;
                        const label =
                            m.senderRole === "reviewer" ? "Reviewer" : "You";
                        const displayLabel = outgoing
                            ? "You"
                            : m.senderName || label;
                        return outgoing ? (
                            // Outgoing message
                            <div
                                key={m.id}
                                className="flex items-start gap-2 justify-end"
                            >
                                <div className="max-w-[75%]">
                                    <div className="bg-[#0077b5] px-3 py-2 text-xs text-white leading-relaxed whitespace-pre-wrap break-words">
                                        {m.messageText}
                                    </div>
                                    <span className="text-[9px] text-[#919191] mt-1 block text-right">
                                        You · {formatTime(m.dateCreated)}
                                    </span>
                                </div>
                                <div className="w-7 h-7 rounded-full bg-[#F47F2E] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                    {initials(m.senderName, m.senderRole)}
                                </div>
                            </div>
                        ) : (
                            // Incoming message
                            <div key={m.id} className="flex items-start gap-2">
                                <div className="w-7 h-7 rounded-full bg-[#0077b5] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                    {initials(m.senderName, m.senderRole)}
                                </div>
                                <div className="max-w-[75%]">
                                    <div className="bg-white border border-[#e0e2e6] px-3 py-2 text-xs text-[#53545a] leading-relaxed whitespace-pre-wrap break-words">
                                        {m.messageText}
                                    </div>
                                    <span className="text-[9px] text-[#919191] mt-1 block">
                                        {displayLabel} ·{" "}
                                        {formatTime(m.dateCreated)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}

                    {isClosed && messages.length > 0 && (
                        <div className="flex items-center justify-center gap-1.5 pt-1 text-[#0077b5]">
                            <FiCheckCircle className="text-xs" />
                            <span className="text-[10px] font-semibold">
                                Accepted by requester
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Input area */}
            <div className="px-3 py-3 border-t border-[#e0e2e6] bg-white flex-shrink-0">
                {error && (
                    <p className="text-[10px] text-red-500 mb-1.5 px-1">
                        {error}
                    </p>
                )}
                <div
                    className={`mx-auto ${chatMaximized ? "max-w-2xl" : ""}`}
                >
                    {/* Reviewer: read-only once the user has accepted. */}
                    {!isUser && isClosed ? (
                        <div className="flex items-center justify-center gap-2 px-3 py-2 bg-[#f0f7ff] border border-[#e0e2e6] text-[#0077b5]">
                            <FiCheckCircle className="text-sm flex-shrink-0" />
                            <span className="text-xs font-semibold">
                                Accepted by requester — conversation closed
                            </span>
                        </div>
                    ) : isUser && !threadOpen ? (
                        // User, no open conversation: only "Need More Information".
                        <button
                            onClick={() => setComposing(true)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#0077b5] text-white text-xs font-semibold hover:bg-[#005a8f] transition-colors"
                        >
                            <FiHelpCircle className="text-sm" />
                            Need More Information
                        </button>
                    ) : (
                        // Composer and Accept sit on one row: input · send · accept.
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Type your message..."
                                disabled={sending || accepting}
                                className="flex-1 min-w-0 border border-[#919191] px-3 py-2 text-xs text-[#53545a] placeholder-[#919191] outline-none focus:border-[#0077b5] disabled:opacity-60"
                            />
                            <button
                                onClick={handleSend}
                                disabled={sending || accepting || !draft.trim()}
                                className="p-2 bg-[#0077b5] text-white hover:bg-[#005a8f] transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Send"
                            >
                                {sending ? (
                                    <FiLoader className="animate-spin text-sm" />
                                ) : (
                                    <FiSend className="text-sm" />
                                )}
                            </button>

                            {/* User only: end the conversation. */}
                            {isUser && (
                                <button
                                    onClick={handleAccept}
                                    disabled={
                                        accepting ||
                                        isClosed ||
                                        messages.length === 0
                                    }
                                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#F47F2E] text-white text-xs font-semibold whitespace-nowrap hover:bg-[#d96c1f] transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Accept the response and close this conversation"
                                >
                                    {accepting ? (
                                        <FiLoader className="animate-spin text-sm" />
                                    ) : (
                                        <FiCheckCircle className="text-sm" />
                                    )}
                                    Accept
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default QuestionChat;
